import { sumDecimalStrings, toDecimalString } from '@/utils/decimal-display';

// The decimal grammar sumDecimalStrings accepts. Checked up front: its
// skip-semantics silently treat a malformed operand as 0, which would turn
// "abc" into a confident full-amount discrepancy.
const DECIMAL_GRAMMAR = /^[+-]?(\d+(\.\d*)?|\.\d+)$/;

/**
 * Discrepancy between the accrued and the actually-paid amount (LR-556).
 *
 * Both values share one sign convention (positive = due to us), so the
 * discrepancy is a direct subtraction: `total_accrued - paid_amount`.
 * Pure string/BigInt via sumDecimalStrings (HON-03) — no float ever touches
 * the authoritative Decimal strings.
 *
 * Returns the signed difference as a Decimal string, or null when `paid` is
 * absent, either operand is malformed, or the difference is exactly zero.
 */
export function paidDiscrepancy(
  totalAccrued: string | number,
  paid: string | number | null | undefined,
): string | null {
  const total = toDecimalString(totalAccrued);
  const paidStr = toDecimalString(paid);
  if (total == null || paidStr == null) return null;
  if (!DECIMAL_GRAMMAR.test(total) || !DECIMAL_GRAMMAR.test(paidStr)) return null;
  const diff = sumDecimalStrings([total, negateDecimalString(paidStr)]);
  return /^-?0*\.?0*$/.test(diff) ? null : diff;
}

/** Negate a Decimal string by sign flipping only — no numeric parsing. */
export function negateDecimalString(value: string): string {
  const trimmed = value.trim();
  if (trimmed.startsWith('-')) return trimmed.slice(1);
  if (trimmed.startsWith('+')) return `-${trimmed.slice(1)}`;
  return `-${trimmed}`;
}

/**
 * Whether two authoritative Decimal strings differ in VALUE (LR-558).
 *
 * `a !== b` is not the same question: DRF renders `Decimal('-0.00')` as
 * `'-0.00'` while the same figure round-tripped through the DB comes back
 * `'0.00'` (SQL numeric has no signed zero), and some fields arrive as JS
 * numbers at runtime (see `toDecimalString`). Both make equal figures compare
 * unequal — which, on a statement-vs-lines cross-check, means telling the user
 * their invoice is broken when it reconciles exactly.
 *
 * Malformed input counts as "no difference": a drift warning is an accusation,
 * and it should not be raised on a value we could not read.
 */
export const decimalStringsDiffer = (
  a: string | number,
  b: string | number | null | undefined,
): boolean => paidDiscrepancy(a, b) !== null;
